import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  lineItemsSelector,
  lineCountSelector,
  isJeLayoutRTLSelector,
  headerLinesNumberSelector,
  jeMismatchedCellsSelector,
} from '@/domain/journal';
import { shouldProcessingDocBeBlockedSelector, isTwoWayMatchOnSelector } from '@/domain/documents';
import { isUiSelectOpenSelector } from '@/domain/ui';
import { rtlEnable } from '@/domain/env';
import { orderByMap } from '@/lib/array';
import { matchCell } from '@/domain/journal/helper';
import elements from '@/components/elements';
import { cleanTableColsWidth } from '@/components/Tables/grid/withResize/helpers';

import VisibilityColsDropdown from '@/components/Tables/layout/JELinesTable/VisibilityColsDropdown/VisibilityColsDropdown';

import withSelectedRows from '@/components/Tables/grid/withSelectedRows';
import withResize from '@/components/Tables/grid/withResize';
import withPinnedCols from '@/components/Tables/grid/withPinnedCols';
import withMatchingColorcoding from '@/components/Tables/grid/withMatchingColorcoding';
import { isMandatory, isActive, getDataCell } from '@/components/Tables/layout/helpers';
import { removeButton } from '@/components/Tables/layout/component';
import FloatGrid from '@/components/Tables/layout/JELinesTable/float';
import DropContainer from '@/components/Tables/layout/JELinesTable/DropContainer';
import JETable from '@/components/Tables/grid/JETable';
import {
  JELinesTableWrapper,
  JELinesTableOverride,
  PinnedContainer,
  PinnedScrolledContainer,
} from '@/components/Tables/layout/JELinesTable/StyledComponents';
import { JE_TABLE_CLASSES } from '@/components/Tables/grid/StyledComponents';

import cx from 'classnames';

const ResizableGrid = compose(withPinnedCols, withResize)(JETable);
const ResizableGridWithMatching = compose(withMatchingColorcoding)(ResizableGrid);
const SelectedRowsGrid = compose(withSelectedRows)(ResizableGrid);
const SelectedRowsGridWithMatching = compose(withMatchingColorcoding)(SelectedRowsGrid);

class JELinesTable extends React.Component {
  boxEl = null;

  rm = removeButton(this.props);

  constructor(props) {
    super(props);
    this.state = {
      hovered: null,
      pinnedColsWidth: {},
      floatColsWidth: {},
    };
  }

  onChangeColsWidth = (gridKey) => (width) => {
    this.setState({ [gridKey]: width });
  };

  onResetColumnsWidth = async () => {
    await cleanTableColsWidth(this.getPinnedStoreKey());
    await cleanTableColsWidth(this.getFloatStoreKey());
    this.setState({ pinnedColsWidth: {}, floatColsWidth: {} });
  };

  getNamedCols() {
    const {
      meta: [cols, rows],
      data,
    } = this.props;
    return this.orderCols(cols).reduce((res, colName) => {
      const cellName = `${colName}${rows[0]}`;
      const title = data.getIn([cellName, 'text']) || data.getIn([cellName, 'cellSet', 'value']);
      return title ? [...res, { title, colName }] : res;
    }, []);
  }

  get getWrapperWidth() {
    return this.boxEl ? this.boxEl.offsetWidth - 30 : null;
  }

  getPinnedStoreKey = () => {
    const { storeKey } = this.props;
    return `pined-table-${storeKey}`;
  };

  getFloatStoreKey = () => {
    const { storeKey } = this.props;
    return `main-table-${storeKey}`;
  };

  getEntry = (col, row) => {
    const { data } = this.props;
    return data.get(`${col}${row}`);
  };

  getRowStyle = (row) => {
    const { active, line } = this.props;
    const { hovered } = this.state;
    const rowClassName = cx({
      [JE_TABLE_CLASSES.ROW_LINE]: line.includes(row),
      [JE_TABLE_CLASSES.ROW_HOVERED]: hovered === row,
    });
    if (typeof active === 'string' && matchCell(active)[1] === row) {
      return cx(rowClassName, JE_TABLE_CLASSES.ROW_ACTIVE);
    }
    return rowClassName;
  };

  getItem = (row, col) => {
    const { getItem, data } = this.props;
    return getItem(this.withDocumentValueHint(getDataCell(data, row, col), col, row));
  };

  // Offer the document's own value on a cell that disagrees with it. Done here rather than in
  // the adapter because the record in the store is the same whether two-way matching is on
  // screen or not, and the toggle is known here.
  //
  // A hint the cell already carries wins: those are validation warnings that can block
  // publishing, so they are never displaced. In practice the two never meet — hints arrive on
  // header fields, never on the line-item fields matching compares.
  withDocumentValueHint = (cell, col, row) => {
    const { mismatchedCells, textractEnabledForDocument } = this.props;
    if (!textractEnabledForDocument || !cell || cell.get('hint')) return cell;

    const documentValue = mismatchedCells.get(`${col}${row}`);

    return documentValue ? cell.set('hint', documentValue) : cell;
  };

  getCellStyle = (col, row) => {
    const { active, mismatchedCells, textractEnabledForDocument } = this.props;

    return cx({
      [JE_TABLE_CLASSES.CELL_MANDATORY]: isMandatory(this.getEntry(col, row)),
      [JE_TABLE_CLASSES.CELL_ACTIVE]: isActive(col, row, active),
      // Gated on the toggle exactly as the line-level highlight is: what the store holds is
      // the same whether two-way matching is on screen or not.
      [JE_TABLE_CLASSES.CELL_MISMATCH]: textractEnabledForDocument && mismatchedCells.has(`${col}${row}`),
    });
  };

  setHovered = (row, status) => {
    const { hovered } = this.state;
    if (status) {
      this.setState({ hovered: row });
    } else {
      this.setState({
        hovered: hovered === row ? null : hovered,
      });
    }
  };

  setBoxEl = (el) => {
    this.boxEl = el;
  };

  orderCols = (cols) => {
    const {
      rtl,
      isJeLayoutRTL,
      order: { float },
      pinnedMeta,
    } = this.props;
    const res = orderByMap(cols, [...pinnedMeta[0], ...float]);
    return rtl !== isJeLayoutRTL ? [...res].reverse() : res;
  };

  fixedColStyle = (col, row) => {
    return cx(this.getCellStyle(col, row), JE_TABLE_CLASSES.CELL_PINNED);
  };

  removeItem = (row) => {
    const { line } = this.props;
    return line.includes(row) ? this.rm(row) : null;
  };

  pinCol = (status) => (col) => {
    const { pinnedCols, onChangePinnedCols } = this.props;
    // Prevent last pinned column unpinning
    if (!status && pinnedCols.length === 1) {
      return;
    }

    const newPinnedCols = status
      ? pinnedCols.includes(col)
        ? pinnedCols
        : [...pinnedCols, col] // Don't add if already pinned
      : pinnedCols.filter((c) => c !== col);
    onChangePinnedCols(newPinnedCols);
  };

  render() {
    const {
      active,
      shouldHideGridSettings,
      onChangeDisabledCols,
      disabledCols,
      pinnedCols,
      forcedEnabledCols,
      permanentlyPinned,
      pinnedMeta,
      floatMeta,
      selectedRows,
      onChangeSelectedRows,
      lineCount,
      isReadOnly,
      isJeLayoutRTL,
      viewArrangement,
      headerLinesNumber,
      gridLineColumnsDictionary,
      onChangeOrder,
      order,
      textractEnabledForDocument,
      rtl,
      isUiSelectOpen,
      hasVisibleColumnsManageButton = true,
    } = this.props;

    const { pinnedColsWidth, floatColsWidth, hovered } = this.state;

    const countAllRows = lineCount - 2; // exclude first title row, and last empty row.
    const [ResizableGridComponent, SelectedRowsGridComponent] = textractEnabledForDocument
      ? [ResizableGridWithMatching, SelectedRowsGridWithMatching]
      : [ResizableGrid, SelectedRowsGrid];

    return (
      <JELinesTableWrapper data-element={elements.je.grid.container} ref={this.setBoxEl}>
        <DropContainer />

        <JELinesTableOverride>
          <PinnedContainer>
            <PinnedScrolledContainer className="scroll-box" sx={{ overflow: isUiSelectOpen ? 'hidden' : 'unset' }}>
              <SelectedRowsGridComponent
                storeKey={this.getPinnedStoreKey()}
                meta={pinnedMeta}
                getRowStyle={this.getRowStyle}
                getCellStyle={this.fixedColStyle}
                getItem={this.getItem}
                prevItem={this.removeItem}
                active={active}
                setHovered={this.setHovered}
                isRtl={isJeLayoutRTL}
                onPinClick={this.pinCol(false)}
                permanentlyPinned={permanentlyPinned}
                pinnedCols={pinnedCols}
                selectedRows={selectedRows}
                startRow={pinnedMeta[1][0]}
                countAllRows={countAllRows}
                onChangeSelectedRows={onChangeSelectedRows}
                selectPlaceCol={pinnedMeta[0][0]}
                isReadOnly={isReadOnly}
                tableColsWidth={pinnedColsWidth}
                onChangeColsWidth={this.onChangeColsWidth('pinnedColsWidth')}
                disabledCols={disabledCols}
                viewArrangement={viewArrangement}
                headerLineNumber={headerLinesNumber}
                lineCount={lineCount}
                wrapperWidth={this.getWrapperWidth}
                gridLineColumnsDictionary={gridLineColumnsDictionary}
                hovered={hovered}
                rtl={rtl}
                {...(textractEnabledForDocument ? { matchingArrowsPosition: 'left' } : {})}
              />
            </PinnedScrolledContainer>
          </PinnedContainer>

          <FloatGrid
            disabledCols={disabledCols}
            pinnedCols={pinnedCols}
            forcedEnabledCols={forcedEnabledCols}
            isJeLayoutRTL={isJeLayoutRTL}
            isRtl={rtl}
            isUiSelectOpen={isUiSelectOpen}
          >
            <ResizableGridComponent
              storeKey={this.getFloatStoreKey()}
              meta={floatMeta}
              getRowStyle={this.getRowStyle}
              getCellStyle={this.getCellStyle}
              getItem={this.getItem}
              active={active}
              setHovered={this.setHovered}
              isRtl={isJeLayoutRTL}
              onPinClick={this.pinCol(true)}
              permanentlyPinned={permanentlyPinned}
              pinnedCols={pinnedCols}
              tableColsWidth={floatColsWidth}
              onChangeColsWidth={this.onChangeColsWidth('floatColsWidth')}
              disabledCols={disabledCols}
              viewArrangement={viewArrangement}
              headerLineNumber={headerLinesNumber}
              lineCount={lineCount}
              wrapperWidth={this.getWrapperWidth}
              gridLineColumnsDictionary={gridLineColumnsDictionary}
              isAutoresize
              hovered={hovered}
            />
          </FloatGrid>
        </JELinesTableOverride>
        {!shouldHideGridSettings && hasVisibleColumnsManageButton && (
          <VisibilityColsDropdown
            disabledCols={disabledCols}
            namedCols={this.getNamedCols()}
            pinnedCols={pinnedCols}
            forcedEnabledCols={forcedEnabledCols}
            onChange={onChangeDisabledCols}
            onResetColumnsWidth={this.onResetColumnsWidth}
            onChangeOrder={onChangeOrder}
            order={order}
          />
        )}
      </JELinesTableWrapper>
    );
  }
}

const mapStateToProps = (state) => ({
  line: lineItemsSelector(state),
  lineCount: lineCountSelector(state),
  isJeLayoutRTL: isJeLayoutRTLSelector(state),
  rtl: rtlEnable(state),
  shouldHideGridSettings: shouldProcessingDocBeBlockedSelector(state),
  headerLinesNumber: headerLinesNumberSelector(state),
  textractEnabledForDocument: isTwoWayMatchOnSelector(state),
  mismatchedCells: jeMismatchedCellsSelector(state),
  isUiSelectOpen: isUiSelectOpenSelector(state),
});

export default compose(connect(mapStateToProps))(JELinesTable);
